import { useEffect } from 'react';
import { RouterProvider } from 'react-router';
import { LazyMotion } from 'motion/react';
import { SessionProvider } from './SessionProvider';
import { AudioProvider } from '@/audio';
import { MicProvider } from '@/audio/MicProvider';
import { ToastHost } from '@/components';
import { router } from './router';
import { registerSyncHandlers } from '@/data/sync-handlers';
import { startSyncWorker } from '@/data/sync-queue';
import { captureInstallPrompt } from '@/pwa/install-prompt';
import { UpdateToast } from '@/pwa/UpdateToast';
import { NativeBridge } from '@/platform/NativeBridge';
import { PrivacyBoot } from '@/features/privacy/PrivacyBoot';
import { applyMotionToDocument, applyThemeToDocument, useUiStore } from '@/stores/ui';

/**
 * Framer Motion's features arrive after first paint. Until they do, every `m`
 * component in the app renders as the plain DOM element it wraps, which is
 * what lets the path screen paint without a 468 kB animation runtime in front
 * of it. Nothing visible is lost: the features land within a frame or two of
 * the first screen, well before anything is tapped.
 */
const loadMotionFeatures = () => import('@/lib/motion-features').then((mod) => mod.default);

export function App() {
  const theme = useUiStore((s) => s.theme);
  const motion = useUiStore((s) => s.motion);

  // Mirror stored preferences onto <html> on first paint and on change.
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    applyMotionToDocument(motion);
  }, [motion]);

  useEffect(() => {
    // Registering before the worker starts, so the first flush has handlers.
    registerSyncHandlers();
    return startSyncWorker();
  }, []);

  // The browser's install prompt fires early and once; it is caught here, in
  // the entry chunk, and spent later by the card on the path.
  useEffect(() => captureInstallPrompt(), []);

  return (
    <LazyMotion features={loadMotionFeatures}>
      <SessionProvider>
        <AudioProvider>
          <MicProvider>
            <RouterProvider router={router} />
            <ToastHost />
            <UpdateToast />
            <NativeBridge />
            <PrivacyBoot />
          </MicProvider>
        </AudioProvider>
      </SessionProvider>
    </LazyMotion>
  );
}
